import state from '../store/globalStore';
import { consumerApikey, consumerApiUrl, atcBaseUrl, apiBaseUrl } from './getConfigs';
import { ErrorType, noticeError } from './newRelic';

export const getFraudStatus = async (authorizationToken: string, consumerId: string = '') => {
  let fraudStatus = {};
  let url = `${apiBaseUrl}/api/psx-lact/user/risk-assessment?consumerId=${consumerId}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: authorizationToken,
      },
    });
    fraudStatus = await response?.json();
  } catch (ex) {
    noticeError(ex, ErrorType.UNKNOWN, {
      methodName: 'getFraudStatus',
    });
    console.log(ex);
    throw {
      ex,
    };
  }
  return fraudStatus;
};

const ongoingRequests = new Map();

export const getOffer = async (listingId: string, baseUrl: string, authorizationToken: string, offerId?: string, buyerId?: string) => {
  let offer;
  if (listingId && authorizationToken.length > 25) {
    const url = `${baseUrl}/api/psx-lact/user/offer`;
    const params = new URLSearchParams({ listingId });
    if (offerId && offerId !== 'undefined') params?.append('offerId', offerId);
    if (buyerId && buyerId !== 'undefined') params?.append('buyerId', buyerId);

    const offerURL = `${url}?${params.toString()}`;
    if (ongoingRequests.has(url)) {
      const { controller } = ongoingRequests.get(url);
      controller.abort('Duplicate request — aborting previous fetch');
    }

    const controller = new AbortController();
    const signal = controller.signal;

    ongoingRequests.set(url, { controller });

    try {
      const response = await fetch(offerURL, {
        method: 'GET',
        headers: {
          Authorization: authorizationToken,
        },
        signal,
      });

      offer = await response?.json();
    } catch (ex: any) {
      const isAbort = ex === 'Duplicate request — aborting previous fetch';

      if (isAbort) {
        // Don't log this — it's an intentional deduped abort
        return;
      }

      // Log everything else
      noticeError(ex, ErrorType.UNKNOWN, {
        methodName: 'getOffer',
      });

      throw {
        listingId,
        ex,
      };
    }
    finally {
      if (ongoingRequests.get(url)?.controller === controller) {
        ongoingRequests.delete(url);
      }
    }
  }
  return offer;
};

/**
 * Used to get list of offers. Mainly used to get the count of active offers
 * @param listingId ID for a listing
 * @param baseUrl Base URL
 * @param authorizationToken Authorization Token of a user
 * @param data List of additional data
 * @returns
 */
export const getOffers = async (listingId: string, baseUrl: string, authorizationToken: string) => {
  let offers;

  if (listingId && authorizationToken.length > 25) {
    let offersURL = `${baseUrl}/api/psx-lact/offers?listingId=${listingId}`;
    try {
      const response = await fetch(offersURL, {
        method: 'GET',
        headers: {
          Authorization: authorizationToken,
        },
      });

      offers = await response?.json();
    } catch (ex) {
      throw {
        listingId,
        ex,
      };
    }
  }
  return offers;
};

/**
 * Used to get list of offers. Mainly used to get the count of active offers
 * @param listingId ID for a listing
 * @param baseUrl Base URL
 * @param authorizationToken Authorization Token of a user
 * @returns
 */
export const getEngagements = async (listingId: string, baseUrl: string, authorizationToken: string) => {
  if (listingId && authorizationToken.length > 25) {
    let offersURL = `${baseUrl}/api/psx-lact/engagements?listingId=${listingId}`;
    try {
      const response = await fetch(offersURL, {
        method: 'GET',
        headers: {
          Authorization: authorizationToken,
        },
      });

      const engagements = await response?.json();
      return engagements;
    } catch (ex) {
      throw {
        listingId,
        ex,
      };
    }
  }
};

export const getEngagementOffer = async (listingId: string, baseUrl: string, authorizationToken: string) => {
  let offer;
  if (listingId && authorizationToken.length > 25) {
    let offerURL = `${baseUrl}/api/psx-lact/user/has-offer?listingId=${listingId}`;
    try {
      const response = await fetch(offerURL, {
        method: 'GET',
        headers: {
          Authorization: authorizationToken,
        },
      });

      offer = await response?.json();
    } catch (ex) {
      noticeError(ex, ErrorType.UNKNOWN, {
        methodName: 'getEngagementOffer',
        listingId,
      });
      throw {
        listingId,
        ex,
      };
    }
  }
  return offer;
};

export async function getConsumerUserZip() {
  const idtoken = state.cognitoIdToken || (await globalThis.PSX_AUTH.getCognitoIdToken());
  const accesstoken = state.cognitoAccessToken || (await globalThis.PSX_AUTH.getCognitoAccessToken());

  // Get user from consumer api to complete user data and get version
  const userResponse: any = await fetch(consumerApiUrl, {
    method: 'GET',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      idtoken,
      accesstoken,
      'x-api-key': consumerApikey,
    },
  });

  const userData = await userResponse.json();

  if (!userResponse.ok || !userData.version) {
    noticeError('Failed to get user from consumer API.', ErrorType.UNKNOWN, {
      methodName: 'getConsumerUserZip',
    });
    throw new Error(`Failed to get user from consumer API.`);
  }

  return userData?.zip;
}

export async function updateConsumerUserZip(zipcode: string) {
  const idtoken = state.cognitoIdToken || (await globalThis.PSX_AUTH.getCognitoIdToken());
  const accesstoken = state.cognitoAccessToken || (await globalThis.PSX_AUTH.getCognitoAccessToken());

  // Get user from consumer api to complete user data and get version.
  const userResponse: any = await fetch(consumerApiUrl, {
    method: 'GET',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      idtoken,
      accesstoken,
      'x-api-key': consumerApikey,
    },
  });

  const userData = await userResponse.json();

  if (!userResponse.ok || !userData.version) {
    noticeError('Failed to update consumer user.', ErrorType.UNKNOWN, {
      methodName: 'updateConsumerUserZip',
    });
    throw new Error(`Failed to update consumer user.`);
  }

  const consumerApiData = {
    firstName: userData.firstname,
    lastName: userData.lastname,
    phone: userData.newPhone,
    zip: zipcode,
    version: userData.version,
  };

  // Setup AT consumer Update API call
  const consumerAPIUpdateResponse = await fetch(consumerApiUrl, {
    method: 'PUT',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      idtoken,
      accesstoken,
      'x-api-key': consumerApikey,
    },
    body: JSON.stringify(consumerApiData),
  });

  console.log('Consumer user update response:', consumerAPIUpdateResponse);

  return consumerAPIUpdateResponse;
}

export async function postPriceDropAlerts(data: any) {
  try {
    const response = await fetch(`${atcBaseUrl}/account-api/lightuser/vehicle`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': consumerApikey,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      console.error(`HTTP error! status: ${response.status}`);
    }
    const savedListing = await response.json();
    return savedListing;
  } catch (error) {
    noticeError(error, ErrorType.UNKNOWN, {
      methodName: 'postPriceDropAlerts',
    });
    console.error('Failed to post price drop alerts', error);
  }
}
